import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, RefObject } from 'react';
import { bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import {
    AGENT_IMAGE_MIME_TYPES,
    FILE_INPUT_ACCEPT,
    LOW_RES_THRESHOLD,
    MAX_FILE_BYTES,
    MAX_UPLOADS,
    VALID_MIME_TYPES,
    compressImage,
    measureLongestEdge,
} from '../upload';
import type { ReportError } from '../messages';
import type { FileUploaderPort, StagedAttachment } from '../upload';

type UseChatAttachmentsOptions = {
    uploader?: FileUploaderPort;
    attachments: StagedAttachment[];
    onAttachmentsAdd?: (added: StagedAttachment[]) => void;
    onAttachmentRemove?: (uploadId: string) => void;
    onError?: ReportError;
};

export type UseChatAttachmentsResult = {
    /** Whether the attach affordance should render at all (an uploader and sink are wired). */
    enabled: boolean;
    /** True while an upload or delete is in flight. */
    isUploading: boolean;
    /** False once the staged set has reached {@link MAX_UPLOADS}. */
    canAttachMore: boolean;
    /** Number of files whose upload is in flight — the shimmer skeleton count. */
    pendingCount: number;
    fileInputRef: RefObject<HTMLInputElement>;
    /** The `accept` attribute for the file input. */
    accept: string;
    fileError: string | null;
    dismissError: () => void;
    lowResWarning: boolean;
    dismissLowResWarning: () => void;
    openFilePicker: () => void;
    onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onRemove: (attachment: StagedAttachment) => void;
};

/**
 * Owns the composer's file-upload interaction: validating a picked batch, warning on
 * low-resolution images, uploading to the injected transport while compressing
 * agent-supported images to inline base64, and cleaning up on removal.
 *
 * Split out of `ChatInput` so the composer stays a text-first component and this
 * concern — validation rules, the two-track upload/compress, the transient error and
 * progress state — lives in one place. The staged set itself is owned upstream (by
 * `useChat`); this hook only turns picked files into staged attachments and hands them up.
 */
export function useChatAttachments({
    uploader,
    attachments,
    onAttachmentsAdd,
    onAttachmentRemove,
    onError,
}: UseChatAttachmentsOptions): UseChatAttachmentsResult {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [lowResWarning, setLowResWarning] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [pendingCount, setPendingCount] = useState(0);

    const isUploading = uploader?.loading ?? false;
    const enabled = !!(uploader && onAttachmentsAdd);
    const canAttachMore = attachments.length < MAX_UPLOADS;

    // Surface upload-hook errors without leaking the raw Error message (often a long
    // signed-URL body).
    useEffect(() => {
        if (uploader?.error) setFileError(__('Failed to upload image. Please try again.'));
    }, [uploader?.error]);

    const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const fileList = event.target.files;
        if (!fileList || fileList.length === 0 || !uploader || !onAttachmentsAdd) return;
        const files = Array.from(fileList);

        // Always reset the input so the same files can be re-selected if the upload fails
        // or the user removes them.
        event.target.value = '';

        if (attachments.length + files.length > MAX_UPLOADS) {
            setFileError(
                __('You can attach a maximum of {{count}} files', { count: String(MAX_UPLOADS) })
            );
            return;
        }

        // Validate up front; reject the whole batch on the first failure so the user sees a
        // clear message rather than silent drops.
        for (const file of files) {
            if (!VALID_MIME_TYPES.has(file.type)) {
                setFileError(
                    __('Please select supported files only (JPEG, PNG, GIF, BMP, PDF, or DOC)')
                );
                return;
            }
            if (file.size > MAX_FILE_BYTES) {
                setFileError(__('"{{fileName}}" is larger than 10MB', { fileName: file.name }));
                return;
            }
        }

        setFileError(null);
        setPendingCount(files.length);

        // Best-effort low-res check — only for agent-supported image types, and only the
        // smallest in the batch since the warning copy is generic.
        const agentImageFiles = files.filter((file) => AGENT_IMAGE_MIME_TYPES.has(file.type));
        try {
            if (agentImageFiles.length > 0) {
                const edges = await Promise.all(agentImageFiles.map(measureLongestEdge));
                setLowResWarning(Math.min(...edges) < LOW_RES_THRESHOLD);
            } else {
                setLowResWarning(false);
            }
        } catch {
            setLowResWarning(false);
        }

        try {
            const [uploaded, compressed] = await Promise.all([
                uploader.uploadFiles(files),
                Promise.all(
                    files.map((file) =>
                        // Only compress agent-supported image types; other files are
                        // uploaded but not forwarded to the agent as inline base64.
                        // Compression failure is non-fatal — the image is still uploaded and
                        // referenced by key, the agent just does not receive it inline.
                        AGENT_IMAGE_MIME_TYPES.has(file.type)
                            ? compressImage(file).catch((error) => {
                                  bugtracker.notify(error);
                                  return null;
                              })
                            : Promise.resolve(null)
                    )
                ),
            ]);
            if (uploaded.length === 0) {
                // The hook surfaced its error via the useEffect above; nothing to add.
                return;
            }
            // Match each upload result back to its source file by name rather than by
            // position — the concrete uploader wired in by ChatBrief.tsx
            // (design-services-common's useFileUploader) splits a batch into PDFs and
            // non-PDFs and uploads them separately, so `uploaded[i]` does not
            // necessarily correspond to `files[i]`/`compressed[i]`. Indices are queued
            // per name (rather than a single map) so a batch with duplicate file names
            // still pairs each result with a distinct source file.
            const indicesByName = new Map<string, number[]>();
            files.forEach((file, index) => {
                const indices = indicesByName.get(file.name);
                if (indices) {
                    indices.push(index);
                } else {
                    indicesByName.set(file.name, [index]);
                }
            });
            const added: StagedAttachment[] = uploaded.map((upload) => {
                const sourceIndex = indicesByName.get(upload.name)?.shift();
                return {
                    uploadId: upload.id,
                    previewUrl: upload.previewUrl,
                    downloadUrl: upload.downloadUrl,
                    fileName: upload.name,
                    mediaType:
                        sourceIndex !== undefined
                            ? files[sourceIndex].type
                            : 'application/octet-stream',
                    fileSize: upload.originalFileSize,
                    base64: sourceIndex !== undefined ? compressed[sourceIndex]?.base64 : undefined,
                    compressedMediaType:
                        sourceIndex !== undefined ? compressed[sourceIndex]?.mediaType : undefined,
                };
            });
            onAttachmentsAdd(added);
        } catch {
            setFileError(__('Failed to upload image. Please try again.'));
        } finally {
            setPendingCount(0);
        }
    };

    const onRemove = (attachment: StagedAttachment) => {
        onAttachmentRemove?.(attachment.uploadId);
        // Best-effort cleanup; failure is non-fatal but worth reporting so we can
        // detect persistent delete failures in error tracking.
        uploader?.deleteFile(attachment.uploadId).catch((error: unknown) => {
            onError?.(error, { context: 'deleteFile', uploadId: attachment.uploadId });
        });
    };

    return {
        enabled,
        isUploading,
        canAttachMore,
        pendingCount,
        fileInputRef,
        accept: FILE_INPUT_ACCEPT,
        fileError,
        dismissError: () => setFileError(null),
        lowResWarning,
        dismissLowResWarning: () => setLowResWarning(false),
        openFilePicker: () => fileInputRef.current?.click(),
        onFileChange,
        onRemove,
    };
}
